import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Modal } from "@/components/ui/Modal";
import {
  LockedAutocomplete,
  type AutocompleteOption,
  type LockedAutocompleteHandle,
} from "@/components/LockedAutocomplete";
import { BookingPeriodDateFields } from "@/components/ui/BookingPeriodDateFields";
import { Button } from "@/components/ui/Button";
import { PartyCountField } from "@/components/ui/PartyCountField";
import { api } from "@/lib/apiClient";
import { DEFAULT_PITCH_CATEGORY_COLOR } from "@/lib/pitchCategoryColors";
import { useArrivalTypes } from "@/lib/useArrivalTypes";
import { usePitchFeatures } from "@/lib/usePitchFeatures";
import { PitchFeatureSelect } from "@/components/pitches/PitchFeatureSelect";
import {
  addDaysLocal,
  dateValidationMessage,
  formatBookingPeriodShort,
  formatIsoDateShort,
  todayLocal,
  validateBookingDates,
} from "@/lib/bookingDates";
import {
  EMPTY_GUEST_FORM,
  GuestDetailsForm,
  guestCreatePayloadToApi,
} from "@/components/guests/GuestDetailsForm";
import {
  EMPTY_VEHICLE_FORM,
  VehicleFields,
  vehicleFieldsToApi,
  type VehicleFormValues,
} from "@/components/bookings/VehicleFields";
import { guestSource } from "@/lib/autocompleteSources";
import {
  bookingPeriodForGuestType,
  DEFAULT_GUEST_TYPE_DEFAULTS,
  guestTypeLabel,
  guestTypeMinNightsError,
  type GuestTypeDefaultsMap,
} from "@/lib/guestType";
import { formatCurrency, formatGrossFromNet } from "@/lib/money";
import type { GuestTypeValue } from "@/types/camping";
import {
  OPERATOR_PITCH_ASSIGNMENT_MODES,
  type PitchAssignmentMode,
} from "@/lib/pitchAssignment";
import type {
  AlternativePeriod,
  AlternativePeriodsResponse,
  AvailabilityResponse,
  Booking,
  Guest,
  GuestCreatePayload,
  Pitch,
  PitchCategory,
  PriceQuote,
} from "@/types/camping";

const inputClass = "eh-input";

/** Platzhalter für Stellplätze ohne Platzkategorie */
export const UNCATEGORIZED_CATEGORY_ID = "__none__";

type Step = 1 | 2 | 3 | 4 | 5;

const STEPS: { id: Step; label: string }[] = [
  { id: 1, label: "Zeitraum" },
  { id: 2, label: "Fahrzeug" },
  { id: 3, label: "Platz" },
  { id: 4, label: "Details" },
  { id: 5, label: "Kontakt" },
];

export interface CreateBookingInitialValues {
  pitchId: string;
  arrivalDate: string;
  departureDate: string;
}

export interface CreateBookingModalProps {
  open: boolean;
  onClose: () => void;
  pitches: Pitch[];
  categories: PitchCategory[];
  onCreated: () => void;
  /** Vorauswahl aus Platzplan (freier Zeitraum). */
  initialValues?: CreateBookingInitialValues | null;
}

export function CreateBookingModal({
  open,
  onClose,
  pitches,
  categories,
  onCreated,
  initialValues = null,
}: CreateBookingModalProps) {
  const today = todayLocal();
  const [step, setStep] = useState<Step>(1);
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [guestOption, setGuestOption] = useState<AutocompleteOption<Guest> | null>(null);
  const guestAutocompleteRef = useRef<LockedAutocompleteHandle>(null);
  const [newGuest, setNewGuest] = useState<GuestCreatePayload>(EMPTY_GUEST_FORM);
  const [creatingNewGuest, setCreatingNewGuest] = useState(false);
  const [assignmentMode, setAssignmentMode] = useState<PitchAssignmentMode>("auto");
  const [categoryId, setCategoryId] = useState("");
  const [fixedPitchId, setFixedPitchId] = useState("");
  const [arrivalType, setArrivalType] = useState("");
  const {
    types: arrivalTypes,
    requiresVehicleLength,
    labelFor: arrivalTypeLabelFor,
  } = useArrivalTypes();
  const [arrivalDate, setArrivalDate] = useState("");
  const [departureDate, setDepartureDate] = useState("");
  const [adults, setAdults] = useState("2");
  const [children, setChildren] = useState("0");
  const [dogs, setDogs] = useState("0");
  const [wantsElectricity, setWantsElectricity] = useState(false);
  const [requestedFeatureIds, setRequestedFeatureIds] = useState<string[]>([]);
  const { features: pitchFeatures } = usePitchFeatures(open);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [alternatives, setAlternatives] = useState<AlternativePeriodsResponse | null>(null);
  const [alternativesLoading, setAlternativesLoading] = useState(false);
  const [alternativesError, setAlternativesError] = useState<string | null>(null);
  const [priceQuote, setPriceQuote] = useState<PriceQuote | null>(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [sendConfirmationEmail, setSendConfirmationEmail] = useState(false);
  const [vehicle, setVehicle] = useState<VehicleFormValues>(EMPTY_VEHICLE_FORM);
  const [guestTypeDefaults, setGuestTypeDefaults] = useState<GuestTypeDefaultsMap>(
    DEFAULT_GUEST_TYPE_DEFAULTS,
  );

  const isNewGuest = selectedGuest === null;
  const activeGuestType: GuestTypeValue =
    selectedGuest?.guest_type ?? newGuest.guest_type ?? "tourist";
  const loadGuestOptions = useCallback((query: string) => guestSource(query), []);

  const dateError = validateBookingDates(arrivalDate, departureDate);
  const departureMin = arrivalDate ? addDaysLocal(arrivalDate, 1) : today;
  const periodValid = Boolean(arrivalDate && departureDate && !dateError);
  const availablePitchItems = availability?.available ?? [];
  const availablePitches = useMemo(() => {
    const ids = new Set(availablePitchItems.map((a) => a.pitch_id));
    return pitches.filter((p) => ids.has(p.id));
  }, [availablePitchItems, pitches]);

  const pitchesInCategory = useMemo(() => {
    if (!categoryId) return [];
    return availablePitches
      .filter((p) =>
        categoryId === UNCATEGORIZED_CATEGORY_ID
          ? !p.category_id
          : p.category_id === categoryId,
      )
      .filter((p) => !wantsElectricity || p.has_electricity)
      .sort((a, b) => a.code.localeCompare(b.code, "de"));
  }, [availablePitches, categoryId, wantsElectricity]);

  const availableFixedPitches = useMemo(() => {
    return availablePitches
      .filter((p) => !wantsElectricity || p.has_electricity)
      .sort((a, b) => a.code.localeCompare(b.code, "de"));
  }, [availablePitches, wantsElectricity]);

  const resolvedPitchId = assignmentMode === "fixed" ? fixedPitchId : "";
  const resolvedCategoryId =
    categoryId && categoryId !== UNCATEGORIZED_CATEGORY_ID ? categoryId : null;

  const bookableByCategoryId = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of availability?.category_bookable ?? []) {
      const key = row.pitch_category_id ?? UNCATEGORIZED_CATEGORY_ID;
      map.set(key, row.bookable_count);
    }
    return map;
  }, [availability?.category_bookable]);

  const categoryOptions = useMemo(() => {
    const active = [...categories]
      .filter((c) => c.is_active)
      .sort((a, b) => a.sort_order - b.sort_order);
    const options = active.map((category) => ({
      category,
      categoryId: category.id,
      freeCount:
        bookableByCategoryId.get(category.id) ??
        availablePitches.filter((p) => p.category_id === category.id).length,
    }));
    const uncategorizedCount =
      bookableByCategoryId.get(UNCATEGORIZED_CATEGORY_ID) ??
      availablePitches.filter((p) => !p.category_id).length;
    if (uncategorizedCount > 0) {
      options.push({
        category: {
          id: UNCATEGORIZED_CATEGORY_ID,
          name: "Ohne Kategorie",
          color: DEFAULT_PITCH_CATEGORY_COLOR,
          sort_order: 9999,
          is_active: true,
        },
        categoryId: UNCATEGORIZED_CATEGORY_ID,
        freeCount: uncategorizedCount,
      });
    }
    return options;
  }, [categories, availablePitches, bookableByCategoryId]);

  const hasBookableCategory = categoryOptions.some((o) => o.freeCount > 0);
  const selectedCategoryOption = categoryOptions.find((o) => o.categoryId === categoryId);
  const needsAlternativePeriods =
    periodValid &&
    !availabilityLoading &&
    availability !== null &&
    !hasBookableCategory;
  const needsCategoryAlternatives =
    periodValid &&
    !availabilityLoading &&
    availability !== null &&
    hasBookableCategory &&
    step === 3 &&
    Boolean(categoryId) &&
    (selectedCategoryOption?.freeCount ?? 0) === 0;
  const shouldFetchAlternatives = needsAlternativePeriods || needsCategoryAlternatives;

  const selectedCategoryLabel = selectedCategoryOption?.category.name ?? "";

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api
      .get<{ guest_type_defaults: GuestTypeDefaultsMap }>("/api/v1/settings")
      .then((settings) => {
        if (!cancelled && settings.guest_type_defaults) {
          setGuestTypeDefaults(settings.guest_type_defaults);
        }
      })
      .catch(() => {
        if (!cancelled) setGuestTypeDefaults(DEFAULT_GUEST_TYPE_DEFAULTS);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const period = bookingPeriodForGuestType("tourist", guestTypeDefaults, today);
    const presetPitch = initialValues?.pitchId
      ? pitches.find((p) => p.id === initialValues.pitchId)
      : undefined;
    const hasPresetPeriod =
      Boolean(initialValues?.arrivalDate) &&
      Boolean(initialValues?.departureDate) &&
      !validateBookingDates(initialValues!.arrivalDate, initialValues!.departureDate);

    setStep(hasPresetPeriod && presetPitch ? 2 : 1);
    setSelectedGuest(null);
    setGuestOption(null);
    setNewGuest(EMPTY_GUEST_FORM);
    setCreatingNewGuest(false);
    if (presetPitch && hasPresetPeriod) {
      setAssignmentMode("fixed");
      setFixedPitchId(presetPitch.id);
      setCategoryId(presetPitch.category_id ?? UNCATEGORIZED_CATEGORY_ID);
      setArrivalDate(initialValues!.arrivalDate);
      setDepartureDate(initialValues!.departureDate);
    } else {
      setAssignmentMode("auto");
      setCategoryId("");
      setFixedPitchId("");
      setArrivalDate(period.arrival);
      setDepartureDate(period.departure);
    }
    setArrivalType("");
    setAdults("2");
    setChildren("0");
    setDogs("0");
    setWantsElectricity(false);
    setRequestedFeatureIds([]);
    setVehicle(EMPTY_VEHICLE_FORM);
    setError(null);
    setSubmitting(false);
    setAvailability(null);
    setAlternatives(null);
    setAlternativesError(null);
    setPriceQuote(null);
    setSendConfirmationEmail(false);
  }, [open, guestTypeDefaults, today, initialValues, pitches]);

  const guestEmailForConfirmation = selectedGuest?.email?.trim() || newGuest.email.trim();

  useEffect(() => {
    if (guestEmailForConfirmation) {
      setSendConfirmationEmail(true);
    }
  }, [guestEmailForConfirmation]);

  useEffect(() => {
    if (!periodValid) {
      setAvailability(null);
      return;
    }
    let cancelled = false;
    setAvailabilityLoading(true);
    api
      .get<AvailabilityResponse>(
        `/api/v1/availability?arrival=${encodeURIComponent(arrivalDate)}&departure=${encodeURIComponent(departureDate)}`,
      )
      .then((data) => {
        if (!cancelled) setAvailability(data);
      })
      .catch(() => {
        if (!cancelled) setAvailability(null);
      })
      .finally(() => {
        if (!cancelled) setAvailabilityLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [periodValid, arrivalDate, departureDate]);

  useEffect(() => {
    if (!shouldFetchAlternatives) {
      setAlternatives(null);
      setAlternativesError(null);
      return;
    }
    const params = new URLSearchParams({
      arrival: arrivalDate,
      departure: departureDate,
    });
    if (needsCategoryAlternatives && categoryId) {
      if (categoryId === UNCATEGORIZED_CATEGORY_ID) {
        params.set("uncategorized_only", "true");
      } else {
        params.set("category_id", categoryId);
      }
    }
    let cancelled = false;
    setAlternativesLoading(true);
    setAlternativesError(null);
    api
      .get<AlternativePeriodsResponse>(`/api/v1/availability/alternatives?${params.toString()}`)
      .then((data) => {
        if (!cancelled) {
          setAlternatives(data);
          setAlternativesError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setAlternatives(null);
          setAlternativesError(
            err instanceof Error ? err.message : "Alternative Zeiträume konnten nicht geladen werden.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setAlternativesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    shouldFetchAlternatives,
    needsCategoryAlternatives,
    categoryId,
    arrivalDate,
    departureDate,
  ]);

  useEffect(() => {
    const canQuote =
      periodValid &&
      (assignmentMode === "fixed"
        ? Boolean(resolvedPitchId)
        : Boolean(categoryId));
    if (!canQuote) {
      setPriceQuote(null);
      return;
    }
    let cancelled = false;
    setPriceLoading(true);
    const quoteBody =
      assignmentMode === "fixed"
        ? {
            pitch_id: resolvedPitchId,
            arrival_date: arrivalDate,
            departure_date: departureDate,
            adults: Number(adults) || 0,
            children: Number(children) || 0,
            dogs: Number(dogs) || 0,
            wants_electricity: wantsElectricity,
            guest_type: activeGuestType,
          }
        : {
            pitch_category_id: resolvedCategoryId,
            arrival_date: arrivalDate,
            departure_date: departureDate,
            adults: Number(adults) || 0,
            children: Number(children) || 0,
            dogs: Number(dogs) || 0,
            wants_electricity: wantsElectricity,
            guest_type: activeGuestType,
          };
    api
      .post<PriceQuote>("/api/v1/pricing/quote", quoteBody)
      .then((data) => {
        if (!cancelled) setPriceQuote(data);
      })
      .catch(() => {
        if (!cancelled) setPriceQuote(null);
      })
      .finally(() => {
        if (!cancelled) setPriceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    periodValid,
    assignmentMode,
    resolvedPitchId,
    resolvedCategoryId,
    categoryId,
    arrivalDate,
    departureDate,
    adults,
    children,
    dogs,
    wantsElectricity,
    activeGuestType,
  ]);

  useEffect(() => {
    if (departureDate && arrivalDate && departureDate <= arrivalDate) {
      setDepartureDate("");
    }
  }, [arrivalDate, departureDate]);

  useEffect(() => {
    if (!categoryId || !periodValid) return;
    if (!categoryOptions.some((o) => o.categoryId === categoryId)) {
      setCategoryId("");
    }
  }, [categoryId, periodValid, categoryOptions]);

  function applyAlternativePeriod(alt: AlternativePeriod, options?: { keepCategory?: boolean }) {
    setArrivalDate(alt.arrival);
    setDepartureDate(alt.departure);
    if (!options?.keepCategory) {
      setCategoryId("");
    }
    setError(null);
  }

  function selectGuest(guest: Guest) {
    setSelectedGuest(guest);
    setCreatingNewGuest(false);
    setGuestOption({
      key: guest.id,
      label: `${guest.last_name}, ${guest.first_name}`,
      value: guest,
    });
    const preservedEmail = guest.email?.trim() || newGuest.email.trim();
    setNewGuest({
      ...EMPTY_GUEST_FORM,
      guest_type: guest.guest_type,
      first_name: guest.first_name,
      last_name: guest.last_name,
      email: preservedEmail,
      phone: guest.phone ?? "",
      street: guest.street ?? "",
      postal_code: guest.postal_code ?? "",
      city: guest.city ?? "",
      country_code: guest.country_code || "DE",
      notes: guest.notes ?? "",
    });
    setError(null);
  }

  function continueAsNewGuest() {
    const parsed = parseGuestSearchName(guestAutocompleteRef.current?.getPendingQuery() ?? "");
    setSelectedGuest(null);
    setCreatingNewGuest(true);
    setNewGuest({
      ...EMPTY_GUEST_FORM,
      first_name: parsed.first_name,
      last_name: parsed.last_name,
    });
    setError(null);
  }

  function continueFromPeriodStep() {
    const validationMsg = dateValidationMessage(dateError);
    if (validationMsg) {
      setError(validationMsg);
      return;
    }
    const minNightsMsg = guestTypeMinNightsError(
      activeGuestType,
      arrivalDate,
      departureDate,
      guestTypeDefaults,
    );
    if (minNightsMsg) {
      setError(minNightsMsg);
      return;
    }
    setStep(2);
    setError(null);
  }

  function continueFromVehicleStep() {
    if (!arrivalType) {
      setError("Bitte die Art der Anreise wählen.");
      return;
    }
    setStep(3);
    setError(null);
  }

  function continueFromCategoryStep() {
    if (assignmentMode === "fixed") {
      if (!fixedPitchId) {
        setError("Bitte einen Stellplatz wählen.");
        return;
      }
      setStep(4);
      setError(null);
      return;
    }
    if (!categoryId) {
      setError("Bitte eine Platzkategorie wählen.");
      return;
    }
    const option = categoryOptions.find((o) => o.categoryId === categoryId);
    if (!option || option.freeCount === 0) {
      setError("In dieser Kategorie ist im gewählten Zeitraum kein Platz frei.");
      return;
    }
    setStep(4);
    setError(null);
  }

  function continueFromPartyStep() {
    if (assignmentMode === "fixed") {
      if (!fixedPitchId) {
        setError("Bitte einen Stellplatz wählen.");
        return;
      }
    } else if (pitchesInCategory.length === 0) {
      setError(
        wantsElectricity
          ? "Kein freier Platz mit Strom in dieser Kategorie – Kategorie oder Stromwunsch anpassen."
          : "Kein freier Platz in dieser Kategorie im gewählten Zeitraum.",
      );
      return;
    }
    setStep(5);
    setError(null);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (isNewGuest && (!newGuest.first_name.trim() || !newGuest.last_name.trim())) {
      setError("Vor- und Nachname sind Pflichtfelder.");
      return;
    }

    const validationMsg = dateValidationMessage(dateError);
    if (validationMsg) {
      setError(validationMsg);
      return;
    }
    if (!arrivalType) {
      setError("Buchungsdaten unvollständig – bitte von vorne prüfen.");
      return;
    }
    if (assignmentMode === "fixed") {
      if (!fixedPitchId) {
        setError("Bitte einen Stellplatz wählen.");
        return;
      }
    } else {
      if (!categoryId) {
        setError("Bitte eine Platzkategorie wählen.");
        return;
      }
      if (pitchesInCategory.length === 0) {
        setError("Kein freier Stellplatz für die gewählte Kategorie verfügbar.");
        return;
      }
    }

    setSubmitting(true);
    try {
      let resolvedGuestId: string;
      if (selectedGuest) {
        resolvedGuestId = selectedGuest.id;
        const emailForGuest = newGuest.email.trim();
        if (emailForGuest && emailForGuest !== (selectedGuest.email?.trim() ?? "")) {
          await api.patch(`/api/v1/guests/${selectedGuest.id}`, { email: emailForGuest });
        }
      } else {
        const created = await api.post<Guest>("/api/v1/guests", guestCreatePayloadToApi(newGuest));
        resolvedGuestId = created.id;
      }

      const bookingBody: Record<string, unknown> = {
        guest_id: resolvedGuestId,
        pitch_assignment_mode: assignmentMode,
        arrival_date: arrivalDate,
        departure_date: departureDate,
        adults: Number(adults) || 0,
        children: Number(children) || 0,
        dogs: Number(dogs) || 0,
        arrival_type: arrivalType,
        wants_electricity: wantsElectricity,
        requested_feature_ids: requestedFeatureIds,
        ...vehicleFieldsToApi(vehicle),
      };
      if (assignmentMode === "fixed") {
        bookingBody.pitch_id = fixedPitchId;
      } else {
        bookingBody.pitch_category_id = resolvedCategoryId;
      }
      const booking = await api.post<Booking>("/api/v1/bookings", bookingBody);
      if (sendConfirmationEmail && guestEmailForConfirmation) {
        try {
          await api.post(`/api/v1/bookings/${booking.id}/send-confirmation`, {});
        } catch (mailErr) {
          setError(
            mailErr instanceof Error
              ? `Buchung angelegt, E-Mail fehlgeschlagen: ${mailErr.message}`
              : "Buchung angelegt, E-Mail konnte nicht gesendet werden.",
          );
          onCreated();
          return;
        }
      }
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler bei der Buchung");
    } finally {
      setSubmitting(false);
    }
  }

  const stepDescription =
    step === 1
      ? "Wann möchten die Gäste anreisen und wieder abreisen?"
      : step === 2
        ? "Womit reisen die Gäste an?"
        : step === 3
          ? "Zuweisungsart und Platz bzw. Kategorie wählen."
          : step === 4
            ? "Personenanzahl festlegen."
            : isNewGuest
              ? "Gast in der Stammkartei suchen oder neu anlegen."
              : "Ausgewählten Gast bestätigen.";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Neue Buchung"
      description={stepDescription}
      size="lg"
    >
      <StepIndicator current={step} />

      <form onSubmit={(e) => void onSubmit(e)} className="space-y-6">
        {step === 1 ? (
          <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-label">Zeitraum</legend>
            <BookingPeriodDateFields
              arrivalId="create-booking-arrival"
              departureId="create-booking-departure"
              arrival={arrivalDate}
              departure={departureDate}
              arrivalMin={today}
              departureMin={departureMin}
              onArrivalChange={setArrivalDate}
              onDepartureChange={setDepartureDate}
              required
            />
            {dateValidationMessage(dateError) ? (
              <p className="text-sm text-red-600">{dateValidationMessage(dateError)}</p>
            ) : null}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-separator"
                checked={wantsElectricity}
                onChange={(e) => setWantsElectricity(e.target.checked)}
              />
              <span className="text-label">Strom gewünscht</span>
            </label>
            {pitchFeatures.length > 0 ? (
              <div className="space-y-1">
                <span className="block text-sm text-label-secondary">
                  Wünsche (werden berücksichtigt, soweit möglich)
                </span>
                <PitchFeatureSelect
                  features={pitchFeatures}
                  selectedIds={requestedFeatureIds}
                  onChange={setRequestedFeatureIds}
                />
              </div>
            ) : null}
            {needsAlternativePeriods ? (
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Im gewählten Zeitraum ist kein Stellplatz verfügbar.
              </p>
            ) : null}
            <AlternativePeriodSuggestions
              show={needsAlternativePeriods}
              loading={alternativesLoading}
              alternatives={alternatives}
              error={alternativesError}
              onSelect={applyAlternativePeriod}
            />
          </fieldset>
        ) : null}

        {step === 2 ? (
          <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-label">Fahrzeug</legend>
            <PeriodSummary
              arrivalDate={arrivalDate}
              departureDate={departureDate}
              onEditPeriod={() => {
                setStep(1);
                setError(null);
              }}
            />
            <div className="grid gap-2 sm:grid-cols-2">
              {arrivalTypes.map((type) => {
                const selected = arrivalType === type.code;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => {
                      setArrivalType(type.code);
                      setError(null);
                    }}
                    className={clsx(
                      "rounded-lg border px-3 py-3 text-left text-sm font-medium transition",
                      selected
                        ? "border-accent bg-accent/10 ring-1 ring-accent text-label"
                        : "border-separator bg-fill-tertiary text-label hover:border-accent/50",
                    )}
                  >
                    {type.name}
                  </button>
                );
              })}
            </div>
            <div className="border-t border-separator pt-3">
              <p className="mb-2 text-sm font-medium text-label">Fahrzeug</p>
              <VehicleFields
                values={vehicle}
                onChange={setVehicle}
                showVehicleLength={requiresVehicleLength(arrivalType)}
                vehicleLengthRequired={requiresVehicleLength(arrivalType)}
              />
            </div>
          </fieldset>
        ) : null}

        {step === 3 ? (
          <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-label">Platz & Kategorie</legend>
            <PeriodSummary
              arrivalDate={arrivalDate}
              departureDate={departureDate}
              onEditPeriod={() => {
                setStep(1);
                setError(null);
              }}
            />
            <div className="space-y-2">
              <p className="text-xs font-medium text-label-secondary">Zuweisungsart</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {OPERATOR_PITCH_ASSIGNMENT_MODES.map((mode) => {
                  const selected = assignmentMode === mode.value;
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => {
                        setAssignmentMode(mode.value);
                        setFixedPitchId("");
                        setCategoryId("");
                        setError(null);
                      }}
                      className={clsx(
                        "rounded-lg border px-3 py-2.5 text-left text-sm transition",
                        selected
                          ? "border-accent bg-accent/10 ring-1 ring-accent"
                          : "border-separator bg-fill-tertiary hover:border-accent/50",
                      )}
                    >
                      <span className="font-medium text-label">{mode.label}</span>
                      <span className="mt-0.5 block text-xs text-label-secondary">
                        {mode.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            {availabilityLoading ? (
              <p className="text-sm text-label-secondary">Verfügbarkeit wird geprüft…</p>
            ) : !periodValid ? (
              <p className="text-sm text-label-secondary">Zuerst einen gültigen Zeitraum wählen.</p>
            ) : needsAlternativePeriods ? (
              <>
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Im gewählten Zeitraum ist kein Stellplatz verfügbar.
                </p>
                <AlternativePeriodSuggestions
                  show={needsAlternativePeriods}
                  loading={alternativesLoading}
                  alternatives={alternatives}
                  error={alternativesError}
                  onSelect={applyAlternativePeriod}
                />
              </>
            ) : assignmentMode === "fixed" ? (
              <>
                <p className="text-sm text-label-secondary">Freie Stellplätze im Zeitraum:</p>
                <ul className="max-h-56 space-y-2 overflow-y-auto" role="listbox" aria-label="Stellplätze">
                  {availableFixedPitches.map((pitch) => {
                    const selected = fixedPitchId === pitch.id;
                    return (
                      <li key={pitch.id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onClick={() => {
                            setFixedPitchId(pitch.id);
                            setCategoryId(pitch.category_id ?? UNCATEGORIZED_CATEGORY_ID);
                            setError(null);
                          }}
                          className={clsx(
                            "w-full rounded-lg border px-3 py-2.5 text-left text-sm transition",
                            selected
                              ? "border-accent bg-accent/10 ring-1 ring-accent"
                              : "border-separator bg-fill-tertiary hover:border-accent/50",
                          )}
                        >
                          <span className="font-medium text-label">{pitchLabel(pitch)}</span>
                          {pitch.category_name ? (
                            <span className="mt-0.5 block text-label-secondary">
                              {pitch.category_name}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {availableFixedPitches.length === 0 ? (
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    Kein freier Stellplatz im gewählten Zeitraum.
                  </p>
                ) : null}
              </>
            ) : (
              <>
                <ul className="space-y-2" role="listbox" aria-label="Platzkategorien">
                  {categoryOptions.map(({ category, categoryId: id, freeCount }) => {
                    const selected = categoryId === id;
                    const unavailable = freeCount === 0;
                    return (
                      <li key={id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onClick={() => {
                            setCategoryId(id);
                            setError(null);
                          }}
                          className={clsx(
                            "w-full rounded-lg border px-3 py-3 text-left text-sm transition",
                            selected
                              ? "border-accent bg-accent/10 ring-1 ring-accent"
                              : "border-separator bg-fill-tertiary hover:border-accent/50",
                            unavailable && !selected && "opacity-80",
                          )}
                        >
                          <span className="font-medium text-label">{category.name}</span>
                          <span className="mt-0.5 block text-label-secondary">
                            {freeCount === 0
                              ? "Keine freien Plätze"
                              : `${freeCount} ${freeCount === 1 ? "Platz frei" : "Plätze frei"}`}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {needsCategoryAlternatives ? (
                  <>
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      In „{selectedCategoryLabel}“ ist im gewählten Zeitraum kein Platz frei.
                    </p>
                    <AlternativePeriodSuggestions
                      show={needsCategoryAlternatives}
                      loading={alternativesLoading}
                      alternatives={alternatives}
                      error={alternativesError}
                      categoryLabel={selectedCategoryLabel}
                      onSelect={(alt) => applyAlternativePeriod(alt, { keepCategory: true })}
                    />
                  </>
                ) : null}
              </>
            )}
            {availability?.wet_blocking_active ? (
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Mindestens ein Platz ist im gewählten Zeitraum durch eine Sperre betroffen.
              </p>
            ) : null}
          </fieldset>
        ) : null}

        {step === 4 ? (
          <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-label">Details</legend>
            <BookingSummaryLine
              arrivalDate={arrivalDate}
              departureDate={departureDate}
              categoryLabel={selectedCategoryLabel}
              arrivalLabel={arrivalTypeLabelFor(arrivalType)}
            />
            <div className="grid gap-3 sm:grid-cols-3">
              <PartyCountField label="Erwachsene" value={adults} onChange={setAdults} />
              <PartyCountField label="Kinder" value={children} onChange={setChildren} />
              <PartyCountField label="Hunde" value={dogs} onChange={setDogs} />
            </div>
            {wantsElectricity ? (
              <p className="text-sm text-label-secondary">Mit Stromanschluss</p>
            ) : null}
            {assignmentMode === "fixed" && fixedPitchId ? (
              <p className="text-sm text-label-secondary">
                Fester Stellplatz: {pitchLabel(pitches.find((p) => p.id === fixedPitchId)!)}
              </p>
            ) : assignmentMode !== "fixed" ? (
              <p className="text-sm text-label-secondary">
                Stellplatz wird später manuell zugewiesen.
                {pitchesInCategory.length > 0
                  ? ` (${pitchesInCategory.length} ${pitchesInCategory.length === 1 ? "Platz" : "Plätze"} frei in Kategorie)`
                  : ""}
              </p>
            ) : (
              <p className="text-sm text-amber-700 dark:text-amber-400" role="alert">
                Bitte einen Stellplatz in Schritt „Platz“ wählen.
              </p>
            )}
            {periodValid &&
            (assignmentMode === "fixed" ? fixedPitchId : categoryId) ? (
              <PricePreview quote={priceQuote} loading={priceLoading} />
            ) : null}
          </fieldset>
        ) : null}

        {step === 5 ? (
          <fieldset className="space-y-4">
            <legend className="text-sm font-medium text-label">Gast</legend>
            <BookingSummaryLine
              arrivalDate={arrivalDate}
              departureDate={departureDate}
              categoryLabel={selectedCategoryLabel}
              arrivalLabel={arrivalTypeLabelFor(arrivalType)}
            />

            <div className="space-y-3 rounded-lg border border-accent/30 bg-accent/5 p-4">
              <LockedAutocomplete
                ref={guestAutocompleteRef}
                label="Gast suchen oder neu anlegen"
                placeholder="Name, E-Mail, Telefon, Ort, Ausweis, Notizen…"
                minChars={2}
                autoFocus
                value={guestOption}
                loadOptions={loadGuestOptions}
                onChange={(option) => {
                  setGuestOption(option);
                  if (option?.value) {
                    selectGuest(option.value);
                  } else {
                    setSelectedGuest(null);
                    setCreatingNewGuest(false);
                    setError(null);
                  }
                }}
              />
              {!guestOption && !creatingNewGuest ? (
                <div className="space-y-2">
                  <p className="text-sm text-label-secondary">
                    Bestehenden Gast suchen oder direkt einen neuen anlegen.
                  </p>
                  <Button type="button" variant="secondary" onClick={continueAsNewGuest}>
                    Neuen Gast anlegen
                  </Button>
                </div>
              ) : null}
            </div>

            {selectedGuest ? (
              <div className="space-y-3">
                <GuestSummaryCard guest={selectedGuest} />
                {!selectedGuest.email?.trim() ? (
                  <label className="text-sm">
                    <span className="mb-1 block text-label-secondary">E-Mail</span>
                    <input
                      type="email"
                      className={inputClass}
                      value={newGuest.email}
                      onChange={(e) => setNewGuest((g) => ({ ...g, email: e.target.value }))}
                      placeholder="für Buchungsbestätigung per E-Mail"
                    />
                  </label>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedGuest(null);
                    setGuestOption(null);
                    setCreatingNewGuest(false);
                    setError(null);
                  }}
                  className="text-sm text-accent hover:underline"
                >
                  Anderen Gast wählen
                </button>
              </div>
            ) : creatingNewGuest ? (
              <GuestDetailsForm
                guest={newGuest}
                onChange={(g) => {
                  setNewGuest(g);
                }}
              />
            ) : null}
            <div className="rounded-lg border border-separator bg-fill-tertiary px-4 py-3">
              {guestEmailForConfirmation ? (
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-separator"
                    checked={sendConfirmationEmail}
                    onChange={(e) => setSendConfirmationEmail(e.target.checked)}
                  />
                  <span className="text-label">
                    Buchungsbestätigung per E-Mail an{" "}
                    <span className="font-medium">{guestEmailForConfirmation}</span> senden
                  </span>
                </label>
              ) : (
                <p className="text-sm text-label-secondary">
                  Für E-Mail-Versand bitte eine E-Mail-Adresse beim Gast hinterlegen.
                </p>
              )}
            </div>
          </fieldset>
        ) : null}

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <div className="eh-modal-actions eh-modal-actions--stacked flex flex-wrap justify-between gap-2 border-t border-separator pt-4">
          <div>
            {step > 1 ? (
              <Button
                type="button"
                variant="secondary"
                className="eh-btn-block-mobile"
                onClick={() => {
                  setStep((s) => (s - 1) as Step);
                  setError(null);
                }}
                disabled={submitting}
              >
                Zurück
              </Button>
            ) : (
              <Button
                type="button"
                variant="secondary"
                className="eh-btn-block-mobile"
                onClick={onClose}
                disabled={submitting}
              >
                Abbrechen
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2 sm:justify-end">
            {step === 1 ? (
              <Button
                type="button"
                className="eh-btn-block-mobile min-w-[8rem] flex-1 sm:flex-none"
                onClick={continueFromPeriodStep}
                disabled={
                  !periodValid ||
                  availabilityLoading ||
                  (needsAlternativePeriods && alternativesLoading)
                }
              >
                Weiter
              </Button>
            ) : null}
            {step === 2 ? (
              <Button
                type="button"
                className="eh-btn-block-mobile flex-1 sm:flex-none"
                onClick={continueFromVehicleStep}
                disabled={!arrivalType}
              >
                Weiter
              </Button>
            ) : null}
            {step === 3 ? (
              <Button
                type="button"
                className="eh-btn-block-mobile flex-1 sm:flex-none"
                onClick={continueFromCategoryStep}
                disabled={
                  availabilityLoading ||
                  (assignmentMode === "fixed"
                    ? !fixedPitchId
                    : !categoryId ||
                      (selectedCategoryOption?.freeCount ?? 0) === 0 ||
                      (needsCategoryAlternatives && alternativesLoading))
                }
              >
                Weiter
              </Button>
            ) : null}
            {step === 4 ? (
              <Button
                type="button"
                className="eh-btn-block-mobile flex-1 sm:flex-none"
                onClick={continueFromPartyStep}
                disabled={
                  assignmentMode === "fixed" ? !fixedPitchId : pitchesInCategory.length === 0
                }
              >
                Weiter
              </Button>
            ) : null}
            {step === 5 ? (
              <Button
                type="submit"
                className="eh-btn-block-mobile flex-1 sm:flex-none"
                loading={submitting}
                disabled={
                  (selectedGuest === null &&
                    (!creatingNewGuest ||
                      !newGuest.first_name.trim() ||
                      !newGuest.last_name.trim())) ||
                  (assignmentMode === "fixed" ? !fixedPitchId : !categoryId)
                }
              >
                Buchung anlegen
              </Button>
            ) : null}
          </div>
        </div>
      </form>
    </Modal>
  );
}

function parseGuestSearchName(query: string): { first_name: string; last_name: string } {
  const parts = query.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: "", last_name: "" };
  if (parts.length === 1) return { first_name: parts[0] ?? "", last_name: "" };
  return {
    first_name: parts.slice(0, -1).join(" "),
    last_name: parts[parts.length - 1] ?? "",
  };
}

function PeriodSummary({
  arrivalDate,
  departureDate,
  onEditPeriod,
}: {
  arrivalDate: string;
  departureDate: string;
  onEditPeriod: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-separator bg-fill-tertiary px-3 py-2 text-sm">
      <span className="text-label-secondary">
        Zeitraum: {formatIsoDateShort(arrivalDate)} → {formatIsoDateShort(departureDate)}
      </span>
      <button
        type="button"
        onClick={onEditPeriod}
        className="shrink-0 text-accent hover:underline"
      >
        Zeitraum ändern
      </button>
    </div>
  );
}

function BookingSummaryLine({
  arrivalDate,
  departureDate,
  categoryLabel,
  arrivalLabel,
}: {
  arrivalDate: string;
  departureDate: string;
  categoryLabel: string;
  arrivalLabel: string;
}) {
  return (
    <p className="text-sm text-label-secondary">
      {formatIsoDateShort(arrivalDate)} → {formatIsoDateShort(departureDate)}
      {categoryLabel ? ` · ${categoryLabel}` : ""}
      {arrivalLabel ? ` · ${arrivalLabel}` : ""}
    </p>
  );
}

function pitchLabel(p: Pitch): string {
  return p.label ? `${p.code} – ${p.label}` : p.code;
}

function PricePreview({ quote, loading }: { quote: PriceQuote | null; loading: boolean }) {
  if (loading) {
    return <p className="text-sm text-label-secondary">Preis wird berechnet…</p>;
  }
  if (!quote) return null;
  if (quote.price_pending) {
    return (
      <div className="rounded-lg border border-dashed border-separator bg-fill-tertiary px-3 py-2.5 text-sm">
        <p className="font-medium text-label">Preis folgt</p>
        <p className="mt-1 text-label-secondary">
          Für diesen Zeitraum ist noch kein Preis veröffentlicht. Die Buchung wird ohne Preisangabe
          gespeichert. Sobald die Preise feststehen, können Sie die Gäste über die Mail-Regeln
          informieren.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-separator bg-fill-tertiary px-3 py-2.5 text-sm">
      <p className="font-medium text-label">Preisvorschau (serverseitig)</p>
      <ul className="mt-2 space-y-1 text-label-secondary">
        {quote.lines.map((line) => (
          <li key={`${line.rule_type}-${line.article_id}`} className="flex justify-between gap-4">
            <span>
              {line.article_name} ({line.quantity} ×{" "}
              {formatGrossFromNet(line.unit_price, line.tax_rate_percent, quote.currency)})
            </span>
            <span className="tabular-nums text-label">
              {formatCurrency(line.line_total, quote.currency)}
            </span>
          </li>
        ))}
      </ul>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-separator pt-2 text-label-secondary">
        <dt className="font-medium text-label">Gesamt (brutto)</dt>
        <dd className="text-right font-medium tabular-nums text-label">
          {formatCurrency(quote.total_amount, quote.currency)}
        </dd>
      </dl>
      <p className="mt-1 text-xs text-label-tertiary">Alle Beträge inkl. MwSt.</p>
    </div>
  );
}

function StepIndicator({ current }: { current: Step }) {
  return (
    <nav aria-label="Buchungsfortschritt" className="mb-5 sm:mb-6">
      <ol className="flex items-center justify-between gap-1 sm:gap-2">
        {STEPS.map((s, index) => {
          const done = s.id < current;
          const active = s.id === current;
          return (
            <li key={s.id} className="flex min-w-0 flex-1 flex-col items-center gap-1 sm:flex-row sm:gap-2">
              <span className="flex items-center gap-1.5 sm:gap-2">
                <span
                  className={clsx(
                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                    done && "bg-accent text-label-on-accent",
                    active && !done && "bg-accent text-label-on-accent ring-2 ring-accent/30",
                    !done && !active && "bg-fill-tertiary text-label-secondary",
                  )}
                  aria-current={active ? "step" : undefined}
                >
                  {done ? "✓" : s.id}
                </span>
                <span
                  className={clsx(
                    "max-w-[3.5rem] truncate text-center text-[0.6rem] font-medium leading-tight sm:max-w-none sm:text-xs",
                    active ? "text-label" : "text-label-secondary",
                  )}
                >
                  {s.label}
                </span>
              </span>
              {index < STEPS.length - 1 ? (
                <span
                  className={clsx(
                    "hidden h-px w-full flex-1 sm:mx-1 sm:block",
                    done ? "bg-accent" : "bg-separator",
                  )}
                  aria-hidden
                />
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function GuestSummaryCard({ guest }: { guest: Guest }) {
  const rows: { label: string; value: string }[] = [
    { label: "Name", value: `${guest.first_name} ${guest.last_name}` },
    { label: "Gästetyp", value: guestTypeLabel(guest.guest_type) },
  ];
  if (guest.email) rows.push({ label: "E-Mail", value: guest.email });
  if (guest.phone) rows.push({ label: "Telefon", value: guest.phone });
  const address = [guest.street, [guest.postal_code, guest.city].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  if (address) rows.push({ label: "Adresse", value: address });
  if (guest.country_code && guest.country_code !== "DE") {
    rows.push({ label: "Land", value: guest.country_code });
  }
  if (guest.notes) rows.push({ label: "Notizen", value: guest.notes });

  return (
    <div className="rounded-lg border border-separator bg-fill-tertiary px-4 py-3 text-sm">
      <dl className="space-y-2">
        {rows.map((row) => (
          <div key={row.label} className="grid grid-cols-1 gap-0.5 sm:grid-cols-[5rem_1fr] sm:gap-2">
            <dt className="text-label-secondary">{row.label}</dt>
            <dd className="text-label">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function endpointShiftLabel(dayShift: number, endpoint: "Anreise" | "Abreise"): string {
  if (dayShift === 0) return `Gleiche ${endpoint}`;
  const days = Math.abs(dayShift);
  const unit = days === 1 ? "Tag" : "Tage";
  if (dayShift > 0) return `${endpoint} ${days} ${unit} später`;
  return `${endpoint} ${days} ${unit} früher`;
}

function alternativePeriodDetailLabel(
  alt: AlternativePeriod,
  requestedNights: number,
): string {
  const departureShift = alt.departure_shift ?? alt.day_shift;
  const parts: string[] = [];
  if (alt.day_shift !== 0 && alt.day_shift === departureShift) {
    const days = Math.abs(alt.day_shift);
    const unit = days === 1 ? "Tag" : "Tage";
    parts.push(
      alt.day_shift > 0 ? `Zeitraum ${days} ${unit} später` : `Zeitraum ${days} ${unit} früher`,
    );
  } else {
    if (alt.day_shift !== 0) {
      parts.push(endpointShiftLabel(alt.day_shift, "Anreise"));
    }
    if (departureShift !== 0) {
      parts.push(endpointShiftLabel(departureShift, "Abreise"));
    }
  }
  if (alt.nights !== requestedNights) {
    parts.push(
      `${alt.nights} ${alt.nights === 1 ? "Nacht" : "Nächte"} (${requestedNights} gewünscht)`,
    );
  }
  return parts.length > 0 ? parts.join(" · ") : "Gleicher Zeitraum";
}

function AlternativePeriodSuggestions({
  show,
  loading,
  alternatives,
  error,
  categoryLabel,
  onSelect,
}: {
  show: boolean;
  loading: boolean;
  alternatives: AlternativePeriodsResponse | null;
  error: string | null;
  categoryLabel?: string;
  onSelect: (alt: AlternativePeriod) => void;
}) {
  if (!show) return null;

  const placesSuffix = categoryLabel ? " in dieser Kategorie" : "";

  if (loading) {
    return (
      <p className="text-sm text-label-secondary">Alternative Zeiträume werden gesucht…</p>
    );
  }

  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }

  if (alternatives && alternatives.alternatives.length === 0) {
    return (
      <p className="text-sm text-label-secondary">
        {categoryLabel
          ? `Für „${categoryLabel}“ wurde in den nächsten 14 Tagen kein freier Zeitraum nahe am gewünschten Datum gefunden.`
          : "In den nächsten 14 Tagen wurde kein freier Zeitraum nahe am gewünschten Datum gefunden."}
      </p>
    );
  }

  if (!alternatives?.alternatives.length) return null;

  return (
    <div className="space-y-2 rounded-lg border border-separator bg-fill-tertiary p-3">
      <p className="text-sm font-medium text-label">
        {categoryLabel
          ? `Alternative Zeiträume für „${categoryLabel}“`
          : "Alternative Zeiträume in der Nähe"}
      </p>
      <p className="text-xs text-label-secondary">
        Möglichst nahe am gewünschten Zeitraum (Wunsch: {alternatives.nights}{" "}
        {alternatives.nights === 1 ? "Nacht" : "Nächte"}).
      </p>
      <ul className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {alternatives.alternatives.map((alt) => (
          <li key={`${alt.arrival}-${alt.departure}`} className="sm:flex-1 sm:min-w-[12rem]">
            <button
              type="button"
              onClick={() => onSelect(alt)}
              className="w-full rounded-lg border border-separator bg-fill px-3 py-2 text-left text-sm transition hover:border-accent/50 hover:bg-accent/5"
            >
              <span className="block font-medium text-label">
                {formatBookingPeriodShort(alt.arrival, alt.departure, alt.nights)}
              </span>
              <span className="mt-0.5 block text-xs text-label-secondary">
                {alternativePeriodDetailLabel(alt, alternatives.nights)} · {alt.available_count}{" "}
                {alt.available_count === 1 ? "Platz frei" : "Plätze frei"}
                {placesSuffix}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
